import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useGetVendorsQuery, useToggleVendorStatusMutation } from "../../features/vendors/vendorApi";
import { useCreateVendorMutation, useUpdateVendorMutation } from "../../features/vendors/vendorApi";
import Table from "../../components/list/Table";
import Pagination from "../../components/list/Pagination";
import type { PaginationState } from "@tanstack/react-table";
import SlideInModal from "../../components/modals/SlideInModal";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormInput from "../../components/forms/FormInput";
import FormSelect from "../../components/forms/FormSelect";
import { FiEdit3, FiPackage, FiPlus, FiSearch } from "react-icons/fi";
import type { Vendor } from "../../features/interfaces";
import GridLoader from "react-spinners/GridLoader";
import VendorRemoteProductsModal from "./components/vendorRemoteProducts/VendorRemoteProductsModal";
import {
  hasVendorIntegration,
  isVendorIntegrationActive,
} from "./components/vendorRemoteProducts/hasVendorIntegration";

const filterFormLabelClass = "text-sm font-semibold text-gray-800 mb-2 block";
const filterFormErrorClass = "text-rose-500 text-xs mt-1.5";
const filterFormInputClass =
  "w-full py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300";

const vendorSchema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").nullable().notRequired(),
  phone: yup.string().nullable().notRequired(),
  address: yup.string().nullable().notRequired(),
  country: yup
    .mixed<{ label: string; value: string } | string>()
    .required("Country is required")
    .test("is-valid-country", "Country must be either USA or CANADA", (value) => {
      if (!value) return false;
      const countryValue = typeof value === 'object' && value !== null && 'value' in value
        ? (value as { label: string; value: string }).value
        : (value as string);
      return countryValue === "USA" || countryValue === "CANADA";
    }),
});

type VendorFormData = yup.InferType<typeof vendorSchema>;

const Vendors = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [vendorToToggle, setVendorToToggle] = useState<Vendor | null>(null);
  const [productsVendor, setProductsVendor] = useState<Vendor | null>(null);
  const [nameSearch, setNameSearch] = useState("");
  const deferredNameSearch = useDeferredValue(nameSearch);

  const { data: vendorsResponse, isLoading, isFetching } = useGetVendorsQuery();
  const [createVendor, { isLoading: isCreating }] = useCreateVendorMutation();
  const [updateVendor, { isLoading: isUpdating }] = useUpdateVendorMutation();
  const [toggleStatus] = useToggleVendorStatusMutation();

  const vendors = vendorsResponse?.data || [];

  const filteredVendors = useMemo(() => {
    const query = deferredNameSearch.trim().toLowerCase();
    if (!query) return vendors;
    return vendors.filter((vendor) =>
      (vendor.name || "").toLowerCase().includes(query)
    );
  }, [vendors, deferredNameSearch]);

  // Reset page only after the deferred filter settles — avoids pagination thrash on each keystroke.
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [deferredNameSearch]);

  const methods = useForm<VendorFormData>({
    resolver: yupResolver(vendorSchema) as any,
    defaultValues: {
      name: "",
      email: undefined,
      phone: undefined,
      address: undefined,
      country: undefined,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = methods;

  const countryOptions = [
    { label: "USA", value: "USA" },
    { label: "CANADA", value: "CANADA" },
  ];

  const handleOpenModal = useCallback(
    (vendor?: Vendor) => {
      if (vendor) {
        setEditingVendor(vendor);
        reset({
          name: vendor.name,
          email: vendor.email || undefined,
          phone: vendor.phone || undefined,
          address: vendor.address || undefined,
          country: vendor.country
            ? { label: vendor.country, value: vendor.country }
            : undefined,
        });
      } else {
        setEditingVendor(null);
        reset({
          name: "",
          email: undefined,
          phone: undefined,
          address: undefined,
          country: undefined,
        });
      }
      setIsModalOpen(true);
    },
    [reset]
  );


  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVendor(null);
    reset();
  };

  const onSubmit = async (data: VendorFormData) => {
    try {
      // Extract value from country select object if it's an object
      const countryValue = typeof data.country === 'object' && data.country !== null && 'value' in data.country
        ? (data.country as { label: string; value: string }).value
        : (data.country as string);

      const vendorData: Partial<Vendor> = {
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        address: data.address || undefined,
        country: countryValue,
        is_active: true,
      };

      if (editingVendor?.vendor_id) {
        await updateVendor({
          id: editingVendor.vendor_id,
          data: vendorData,
        }).unwrap();
      } else {
        await createVendor(vendorData).unwrap();
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving vendor:", error);
    }
  };

  const handleToggleClick = useCallback((vendor: Vendor) => {
    setVendorToToggle(vendor);
    setShowConfirmModal(true);
  }, []);

  const handleConfirmToggle = async () => {
    if (vendorToToggle?.vendor_id) {
      try {
        await toggleStatus({
          id: vendorToToggle.vendor_id,
          is_active: !vendorToToggle.is_active,
        }).unwrap();
        setShowConfirmModal(false);
        setVendorToToggle(null);
      } catch (error) {
        console.error("Error toggling status:", error);
        setShowConfirmModal(false);
        setVendorToToggle(null);
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        cell: (info: any) => (
          <span className="text-gray-600 font-medium">
            {info.getValue() || <span className="text-gray-400">-</span>}
          </span>
        ),
        meta: { align: "left" as const },
      },
      {
        id: "email",
        header: "Email",
        accessorKey: "email",
        cell: (info: any) => (
          <span className="text-gray-600 font-medium">
            {info.getValue() || <span className="text-gray-400">-</span>}
          </span>
        ),
        meta: { align: "left" as const },
      },
      {
        id: "phone",
        header: "Phone",
        accessorKey: "phone",
        cell: (info: any) => (
          <span className="text-gray-600 font-medium">
            {info.getValue() || <span className="text-gray-400">-</span>}
          </span>
        ),
        meta: { align: "left" as const },
      },
      {
        id: "country",
        header: "Country",
        accessorKey: "country",
        cell: (info: any) => (
          <span className="text-gray-600 font-medium">
            {info.getValue() || <span className="text-gray-400">-</span>}
          </span>
        ),
        meta: { align: "left" as const },
      },
      {
        id: "is_active",
        header: "Status",
        accessorKey: "is_active",
        cell: (info: any) => {
          const isActive = info.getValue();
          const vendor = info.row.original as Vendor;
          return (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => handleToggleClick(vendor)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:ring-offset-2 ${
                  isActive ? "bg-[#dbb685]" : "bg-gray-300"
                }`}
                role="switch"
                aria-checked={isActive}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          );
        },
        meta: { align: "center" as const },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }: any) => {
          const vendor = row.original as Vendor;
          const canViewProducts = hasVendorIntegration(vendor);
          const integrationActive = isVendorIntegrationActive(vendor);

          return (
            <div className="flex justify-center items-center gap-1.5">
              {canViewProducts && (
                <button
                  type="button"
                  className="px-2 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-[#6b5438] hover:bg-[#faf6f0] cursor-pointer border border-[#e8d5b8] bg-[#faf6f0] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setProductsVendor(vendor)}
                  disabled={!integrationActive}
                  title={
                    integrationActive
                      ? "View products from integration"
                      : "Integration is inactive"
                  }
                >
                  <FiPackage size={14} />
                  <span className="text-xs font-medium">Products</span>
                </button>
              )}
              <button
                type="button"
                className="px-2 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-blue-600 hover:bg-blue-50 cursor-pointer border border-blue-200 bg-blue-50/50"
                onClick={() => handleOpenModal(vendor)}
                title="Edit"
              >
                <FiEdit3 size={14} />
                <span className="text-xs font-medium">Edit</span>
              </button>
            </div>
          );
        },
        meta: { align: "center" as const },
      },
    ],
    [handleToggleClick, handleOpenModal]
  );

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-600 mb-1">Vendors</h1>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 text-white rounded-lg font-semibold flex items-center justify-center gap-2 text-sm cursor-pointer"
            style={{ 
              minWidth: '130px', 
              minHeight: '38px',
              backgroundColor: '#dbb685',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#c9a570';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#dbb685';
            }}
          >
            <FiPlus size={16} />
            <span>Add Vendor</span>
          </button>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <div className="relative w-full max-w-md">
            <FiSearch
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              placeholder="Search by vendor name"
              className="w-full py-2.5 pl-9 pr-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <GridLoader color="#dbb685" />
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                data={filteredVendors}
                isSearchable={false}
                isSortable={true}
                isFetching={isFetching}
                pagination={true}
                paginationState={pagination}
                setPaginationState={setPagination}
                tableClass="w-full"
                tdClass="text-sm py-4 px-6 text-gray-700 font-medium"
                theadRowClass="bg-gray-100 border-b border-gray-200"
              />
              <div className="mt-6 flex justify-between items-center bg-white rounded-lg border border-gray-200 p-4 mx-4 mb-4">
                <div className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold text-gray-800">
                    {filteredVendors.length === 0
                      ? 0
                      : pagination.pageIndex * pagination.pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-gray-800">
                    {Math.min(
                      (pagination.pageIndex + 1) * pagination.pageSize,
                      filteredVendors.length
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-800">
                    {filteredVendors.length}
                  </span>{" "}
                  vendors
                </div>
                <Pagination
                  currentPage={pagination.pageIndex + 1}
                  totalItems={filteredVendors.length}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={(page: number) => {
                    setPagination({
                      ...pagination,
                      pageIndex: page - 1,
                    });
                  }}
                  btnClass="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
                  pageNumClass="text-sm px-3 py-1"
                  activePageNumClass="text-white rounded-lg font-semibold"
                  activePageNumStyle={{ backgroundColor: "#dbb685" }}
                  inactivePageNumClass="hover:bg-gray-100 rounded-lg text-gray-700 font-medium"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <SlideInModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingVendor ? "Edit Vendor" : "Add Vendor"}
        width="600px"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
            <FormInput
              label="Name"
              name="name"
              placeholder="Enter vendor name"
              register={register}
              errors={errors}
              labelClassName={filterFormLabelClass}
              className={filterFormInputClass}
              errorClassName={filterFormErrorClass}
            />

            <FormInput
              label="Email"
              name="email"
              type="email"
              placeholder="Enter email"
              register={register}
              errors={errors}
              labelClassName={filterFormLabelClass}
              className={filterFormInputClass}
              errorClassName={filterFormErrorClass}
            />

            <FormInput
              label="Phone"
              name="phone"
              placeholder="Enter phone number"
              register={register}
              errors={errors}
              labelClassName={filterFormLabelClass}
              className={filterFormInputClass}
              errorClassName={filterFormErrorClass}
            />

            <FormInput
              label="Address"
              name="address"
              type="textarea"
              placeholder="Enter address"
              register={register}
              errors={errors}
              labelClassName={filterFormLabelClass}
              className={filterFormInputClass}
              errorClassName={filterFormErrorClass}
            />

            <FormSelect
              label="Country"
              name="country"
              placeholder="Select country"
              control={control as any}
              options={countryOptions}
              labelClassName={filterFormLabelClass}
              className="border border-gray-200 rounded-lg text-sm shadow-sm"
              borderColor="white"
              errorClassName={filterFormErrorClass}
            />

            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors shadow-sm"
                style={{ minWidth: '100px', minHeight: '40px' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating || isUpdating}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ 
                  minWidth: '120px', 
                  minHeight: '40px',
                  backgroundColor: '#dbb685',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isCreating && !isUpdating) {
                    e.currentTarget.style.backgroundColor = '#c9a570';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCreating && !isUpdating) {
                    e.currentTarget.style.backgroundColor = '#dbb685';
                  }
                }}
              >
                {isCreating || isUpdating ? (
                  <span>Saving...</span>
                ) : (
                  <span>{editingVendor ? "Update" : "Create"}</span>
                )}
              </button>
            </div>
          </form>
        </FormProvider>
      </SlideInModal>

      {/* Confirmation Modal for Toggle Status */}
      {showConfirmModal && vendorToToggle && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={() => {
              setShowConfirmModal(false);
              setVendorToToggle(null);
            }}
          ></div>
          <div className="fixed inset-0 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {vendorToToggle.is_active ? "Deactivate Vendor" : "Activate Vendor"}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to {vendorToToggle.is_active ? "deactivate" : "activate"} the vendor{" "}
                <span className="font-semibold text-gray-900">"{vendorToToggle.name}"</span>? 
                {vendorToToggle.is_active 
                  ? " This vendor will no longer be available for selection." 
                  : " This vendor will become available for selection."}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setVendorToToggle(null);
                  }}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmToggle}
                  className={`px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all ${
                    vendorToToggle.is_active
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {vendorToToggle.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <VendorRemoteProductsModal
        vendor={productsVendor}
        isOpen={!!productsVendor}
        onClose={() => setProductsVendor(null)}
      />
    </>
  );
};

export default Vendors;
